import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import i18n from '../../../../../i18n';

import { makeStyles } from '@material-ui/core/styles';
import { Text, TextTypography } from 'components/Text/Text';
import { SmallButton } from 'components/SmallButton/SmallButton';
import { RoutePath } from 'RoutePath';
import clsx from 'clsx';
import { makeUrl } from 'core/utils/utils';
import { Endpoint } from 'Endpoint';
import { getRequest, putRequest } from 'core/axios/axios';
import { useSelector } from 'react-redux';
import { authStoreIdSelector } from 'state/auth/selectors';

import { ReactComponent as PlusIcon } from 'assets/icons/main-16/plus--16.svg';
import { ReactComponent as CloseIcon } from 'assets/icons/main-16/times--16.svg';
import { ReactComponent as SettingsIcon } from 'assets/icons/main-16/cog--16.svg';
import { ReactComponent as SubmitIcon } from 'assets/icons/status/check-square--16.svg';
import { Hidden } from '@material-ui/core';
import { userMenuProfileSelector } from 'state/userMenu/selector';
import { storeSelector } from '../../../../state/store/selectors';
import { SupportedCountryCodes } from 'types';
import browserCookies from 'browser-cookies';

const isServer = typeof window === 'undefined';

export interface FullnessWidgetData {
    completePercent: number;
    catalogCompleted: boolean;
    deliveryCompleted: boolean;
    paymentMethodsCompleted: boolean;
    descriptionsCompleted: boolean;
    galleryCompleted: boolean;
    instagramCompleted: boolean;
    sellingDescriptionCompleted: boolean;
    sloganCompleted: boolean;
}

const useStyles = makeStyles(theme => ({
    full: {
        flexBasis: '100%',
        margin: theme.spacing(1.5),
        display: ({ completePercent }: { completePercent: number }) => {
            switch (completePercent) {
                case 100:
                    return 'none';
            }
        },
    },
    container: {
        width: '100%',
        height: '100%',
        background: ({ completePercent }: { completePercent: number }) => {
            switch (completePercent) {
                case 23:
                    return 'rgba(255, 113, 113, 0.1)';
                case 34:
                    return 'rgba(255, 114, 0, 0.1)';
                case 45:
                    return 'rgba(253, 230, 27, 0.1)';
                case 56:
                    return 'rgba(255, 113, 113, 0.1)';
                case 67:
                    return 'rgba(255, 114, 0, 0.1)';
                case 78:
                    return 'rgba(253, 230, 27, 0.1)';
                case 89:
                    return 'rgba(255, 113, 113, 0.1)';
                case 100:
                    return 'rgba(215, 248, 195, 1)';
            }
        },
        borderRadius: 20,
        display: 'flex',
        flexDirection: 'column',
    },
    title: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: theme.spacing(3),
        borderBottom: '1px solid rgba(15, 24, 37, 0.05)',
    },
    progressBar: {
        width: 218,
        height: 8,
        borderRadius: 8,
        background: 'rgba(15, 24, 37, 0.1)',
    },
    progressDynamic: (params: { completePercent: number }) => {
        switch (params.completePercent) {
            case 23:
                return { width: 30, height: 8, background: '#FF7171' };
            case 34:
                return { width: 57, background: '#FF7200' };
            case 45:
                return { width: 84, background: '#FDE61B' };
            case 56:
                return { width: 111, background: '#FF7171' };
            case 67:
                return { width: 138, background: '#FF7200' };
            case 78:
                return { width: 165, background: '#FDE61B' };
            case 89:
                return { width: 192, background: '#FF7171' };
            case 100:
                return {
                    width: 218,
                    background: '#75C345',
                };
        }
    },
    progressStatic: {
        height: 8,
        borderRadius: 8,
    },
    elementsContainer: {
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3),
    },
    element: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingTop: theme.spacing(2.5),
        paddingBottom: theme.spacing(2.5),
        borderBottom: '1px solid rgba(15, 24, 37, 0.05)',
        '&:last-child': {
            borderBottom: 'none',
        },
    },
    buttons: {
        display: 'flex',
    },
    hide: {
        marginLeft: theme.spacing(2),
    },
}));

export const FullnessWidget = () => {
    const history = useHistory();
    const storeId = useSelector(authStoreIdSelector);
    const userProfile = useSelector(userMenuProfileSelector);
    const storeProfile = useSelector(storeSelector);
    const [fullnessData, setFullnessData] = useState<FullnessWidgetData>({
        completePercent: 15,
        catalogCompleted: false,
        deliveryCompleted: false,
        paymentMethodsCompleted: false,
        descriptionsCompleted: false,
        galleryCompleted: false,
        instagramCompleted: false,
        sellingDescriptionCompleted: false,
        sloganCompleted: false,
    });

    useEffect(() => {
        const url = makeUrl(
            Endpoint.FULLNESS_INFO,
            {
                storeId,
            },
            {},
        );
        getRequest(url)
            .then((response: { data: FullnessWidgetData }) => {
                setFullnessData(response.data);
            })
            .catch(console.log);
    }, [storeId]);
    const handleHide = useCallback(
        (
            deliveryCompleted: boolean,
            paymentMethodsCompleted: boolean,
            descriptionsCompleted: boolean,
            galleryCompleted: boolean,
        ) => {
            const url = makeUrl(
                Endpoint.FULLNESS_INFO,
                {
                    storeId,
                },
                {},
            );
            putRequest(url, { deliveryCompleted, paymentMethodsCompleted, descriptionsCompleted, galleryCompleted })
                .then((response: { data: FullnessWidgetData }) => {
                    setFullnessData(response.data);
                })
                .catch(console.log);
        },
        [storeId],
    );
    const classes = useStyles({ completePercent: fullnessData?.completePercent });
    const { t } = i18n.useTranslation();
    const isoCode = !isServer ? browserCookies?.get('country') : SupportedCountryCodes.RUS;

    return (
        <div className={classes.full}>
            <div className={classes.container}>
                <div className={classes.title}>
                    <Text typography={TextTypography.LEAD_ACCENT}>
                        {t('vendor:dashboard:fullnessWidget:title') + fullnessData?.completePercent + '%'}
                    </Text>
                    <Hidden xsDown>
                        <div className={classes.progressBar}>
                            <div className={clsx(classes.progressDynamic, classes.progressStatic)} />
                        </div>
                    </Hidden>
                </div>
                <div className={classes.elementsContainer}>
                    {!userProfile?.email && (
                        <div className={classes.element}>
                            <Text typography={TextTypography.BODY}>{t('vendor:dashboard:fullnessWidget:email')}</Text>
                            <Hidden xsDown>
                                <SmallButton
                                    text={'vendor:dashboard:fullnessWidget:add'}
                                    width={110}
                                    color={'#2697FF'}
                                    hoverColor={'#1b64a7'}
                                    onClick={() => history.push(RoutePath.VENDOR_MENU_PROFILE)}
                                />
                            </Hidden>
                            <Hidden smUp>
                                <SmallButton
                                    icon={PlusIcon}
                                    width={32}
                                    color={'#2697FF'}
                                    hoverColor={'#1b64a7'}
                                    onClick={() => history.push(RoutePath.VENDOR_MENU_PROFILE)}
                                />
                            </Hidden>
                        </div>
                    )}
                    {!fullnessData?.catalogCompleted && (
                        <div className={classes.element}>
                            <Text typography={TextTypography.BODY}>{t('vendor:dashboard:fullnessWidget:catalog')}</Text>
                            <Hidden xsDown>
                                <SmallButton
                                    text={'vendor:dashboard:fullnessWidget:add'}
                                    width={110}
                                    color={'#2697FF'}
                                    hoverColor={'#1b64a7'}
                                    onClick={() => history.push(RoutePath.VENDOR_CATALOG_ADD)}
                                />
                            </Hidden>
                            <Hidden smUp>
                                <SmallButton
                                    icon={PlusIcon}
                                    width={32}
                                    color={'#2697FF'}
                                    hoverColor={'#1b64a7'}
                                    onClick={() => history.push(RoutePath.VENDOR_CATALOG_ADD)}
                                />
                            </Hidden>
                        </div>
                    )}
                    {!fullnessData?.deliveryCompleted && (
                        <div className={classes.element}>
                            <Text typography={TextTypography.BODY}>
                                {t('vendor:dashboard:fullnessWidget:delivery')}
                            </Text>
                            <div className={classes.buttons}>
                                <Hidden xsDown>
                                    <SmallButton
                                        text={'vendor:dashboard:fullnessWidget:submit'}
                                        width={140}
                                        color={'#2697FF'}
                                        hoverColor={'#1b64a7'}
                                        onClick={() => history.push(RoutePath.VENDOR_MENU_SETTINGS_DELIVERY)}
                                    />
                                </Hidden>
                                <Hidden smUp>
                                    <SmallButton
                                        icon={SubmitIcon}
                                        width={32}
                                        color={'#2697FF'}
                                        hoverColor={'#1b64a7'}
                                        onClick={() => history.push(RoutePath.VENDOR_MENU_SETTINGS_DELIVERY)}
                                    />
                                </Hidden>
                                <div className={classes.hide}>
                                    <Hidden xsDown>
                                        <SmallButton
                                            text={'vendor:dashboard:fullnessWidget:hide'}
                                            width={94}
                                            color={'rgba(36, 49, 67, 0.6)'}
                                            hoverColor={'rgba(36, 49, 67, 1)'}
                                            onClick={() =>
                                                handleHide(
                                                    true,
                                                    fullnessData?.paymentMethodsCompleted,
                                                    fullnessData?.descriptionsCompleted,
                                                    fullnessData?.galleryCompleted,
                                                )
                                            }
                                        />
                                    </Hidden>
                                    <Hidden smUp>
                                        <SmallButton
                                            icon={CloseIcon}
                                            width={32}
                                            color={'rgba(36, 49, 67, 0.6)'}
                                            hoverColor={'rgba(36, 49, 67, 1)'}
                                            onClick={() =>
                                                handleHide(
                                                    true,
                                                    fullnessData?.paymentMethodsCompleted,
                                                    fullnessData?.descriptionsCompleted,
                                                    fullnessData?.galleryCompleted,
                                                )
                                            }
                                        />
                                    </Hidden>
                                </div>
                            </div>
                        </div>
                    )}
                    {!fullnessData?.paymentMethodsCompleted && (
                        <div className={classes.element}>
                            <Text typography={TextTypography.BODY}>{t('vendor:dashboard:fullnessWidget:payment')}</Text>
                            <div className={classes.buttons}>
                                <Hidden xsDown>
                                    <SmallButton
                                        text={'vendor:dashboard:fullnessWidget:settings'}
                                        width={118}
                                        color={'#2697FF'}
                                        hoverColor={'#1b64a7'}
                                        onClick={() => history.push(RoutePath.VENDOR_MENU_SETTINGS)}
                                    />
                                </Hidden>
                                <Hidden smUp>
                                    <SmallButton
                                        icon={SettingsIcon}
                                        width={32}
                                        color={'#2697FF'}
                                        hoverColor={'#1b64a7'}
                                        onClick={() => history.push(RoutePath.VENDOR_MENU_SETTINGS)}
                                    />
                                </Hidden>
                                <div className={classes.hide}>
                                    <Hidden xsDown>
                                        <SmallButton
                                            text={'vendor:dashboard:fullnessWidget:hide'}
                                            width={94}
                                            color={'rgba(36, 49, 67, 0.6)'}
                                            hoverColor={'rgba(36, 49, 67, 1)'}
                                            onClick={() =>
                                                handleHide(
                                                    fullnessData?.deliveryCompleted,
                                                    true,
                                                    fullnessData?.descriptionsCompleted,
                                                    fullnessData?.galleryCompleted,
                                                )
                                            }
                                        />
                                    </Hidden>
                                    <Hidden smUp>
                                        <SmallButton
                                            icon={CloseIcon}
                                            width={32}
                                            color={'rgba(36, 49, 67, 0.6)'}
                                            hoverColor={'rgba(36, 49, 67, 1)'}
                                            onClick={() =>
                                                handleHide(
                                                    fullnessData?.deliveryCompleted,
                                                    true,
                                                    fullnessData?.descriptionsCompleted,
                                                    fullnessData?.galleryCompleted,
                                                )
                                            }
                                        />
                                    </Hidden>
                                </div>
                            </div>
                        </div>
                    )}
                    {!fullnessData?.sloganCompleted && (
                        <div className={classes.element}>
                            <Text typography={TextTypography.BODY}>{t('vendor:dashboard:fullnessWidget:slogan')}</Text>
                            <div className={classes.buttons}>
                                <Hidden xsDown>
                                    <SmallButton
                                        text={'vendor:dashboard:fullnessWidget:add'}
                                        width={110}
                                        color={'#2697FF'}
                                        hoverColor={'#1b64a7'}
                                        onClick={() => {
                                            const url = makeUrl(
                                                RoutePath.VENDOR_MENU_STORE_INFO,
                                                {},
                                                {
                                                    id: 'slogan',
                                                },
                                            );
                                            history.push(url);
                                        }}
                                    />
                                </Hidden>
                                <Hidden smUp>
                                    <SmallButton
                                        icon={SubmitIcon}
                                        width={32}
                                        color={'#2697FF'}
                                        hoverColor={'#1b64a7'}
                                        onClick={() => {
                                            const url = makeUrl(
                                                RoutePath.VENDOR_MENU_STORE_INFO,
                                                {},
                                                {
                                                    id: 'slogan',
                                                },
                                            );
                                            history.push(url);
                                        }}
                                    />
                                </Hidden>
                            </div>
                        </div>
                    )}
                    {!fullnessData?.sellingDescriptionCompleted && (
                        <div className={classes.element}>
                            <Text typography={TextTypography.BODY}>
                                {t('vendor:dashboard:fullnessWidget:sellingOffer')}
                            </Text>
                            <div className={classes.buttons}>
                                <Hidden xsDown>
                                    <SmallButton
                                        text={'vendor:dashboard:fullnessWidget:add'}
                                        width={110}
                                        color={'#2697FF'}
                                        hoverColor={'#1b64a7'}
                                        onClick={() => {
                                            const url = makeUrl(
                                                RoutePath.VENDOR_MENU_STORE_INFO,
                                                {},
                                                {
                                                    id: 'sellingOffer',
                                                },
                                            );
                                            history.push(url);
                                        }}
                                    />
                                </Hidden>
                                <Hidden smUp>
                                    <SmallButton
                                        icon={SubmitIcon}
                                        width={32}
                                        color={'#2697FF'}
                                        hoverColor={'#1b64a7'}
                                        onClick={() => {
                                            const url = makeUrl(
                                                RoutePath.VENDOR_MENU_STORE_INFO,
                                                {},
                                                {
                                                    id: 'sellingOffer',
                                                },
                                            );
                                            history.push(url);
                                        }}
                                    />
                                </Hidden>
                            </div>
                        </div>
                    )}
                    {!fullnessData?.descriptionsCompleted && (
                        <div className={classes.element}>
                            <Text typography={TextTypography.BODY}>
                                {t('vendor:dashboard:fullnessWidget:description')}
                            </Text>
                            <div className={classes.buttons}>
                                <Hidden xsDown>
                                    <SmallButton
                                        text={'vendor:dashboard:fullnessWidget:add'}
                                        width={110}
                                        color={'#2697FF'}
                                        hoverColor={'#1b64a7'}
                                        onClick={() => {
                                            const url = makeUrl(
                                                RoutePath.VENDOR_MENU_STORE_INFO,
                                                {},
                                                {
                                                    id: 'description',
                                                },
                                            );
                                            history.push(url);
                                        }}
                                    />
                                </Hidden>
                                <Hidden smUp>
                                    <SmallButton
                                        icon={SubmitIcon}
                                        width={32}
                                        color={'#2697FF'}
                                        hoverColor={'#1b64a7'}
                                        onClick={() => {
                                            const url = makeUrl(
                                                RoutePath.VENDOR_MENU_STORE_INFO,
                                                {},
                                                {
                                                    id: 'description',
                                                },
                                            );
                                            history.push(url);
                                        }}
                                    />
                                </Hidden>
                            </div>
                        </div>
                    )}
                    {isoCode !== SupportedCountryCodes.RUS && !fullnessData?.instagramCompleted && (
                        <div className={classes.element}>
                            <Text typography={TextTypography.BODY}>
                                {t('vendor:dashboard:fullnessWidget:instagram')}
                            </Text>
                            <div className={classes.buttons}>
                                <Hidden xsDown>
                                    <SmallButton
                                        text={'vendor:dashboard:fullnessWidget:add'}
                                        width={110}
                                        color={'#2697FF'}
                                        hoverColor={'#1b64a7'}
                                        onClick={() => {
                                            const url = makeUrl(
                                                RoutePath.VENDOR_MENU_STORE_INFO,
                                                {},
                                                {
                                                    id: 'instagram',
                                                },
                                            );
                                            history.push(url);
                                        }}
                                    />
                                </Hidden>
                                <Hidden smUp>
                                    <SmallButton
                                        icon={SubmitIcon}
                                        width={32}
                                        color={'#2697FF'}
                                        hoverColor={'#1b64a7'}
                                        onClick={() => {
                                            const url = makeUrl(
                                                RoutePath.VENDOR_MENU_STORE_INFO,
                                                {},
                                                {
                                                    id: 'instagram',
                                                },
                                            );
                                            history.push(url);
                                        }}
                                    />
                                </Hidden>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
